import type { FC } from 'react';
import React from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Label, Link, OneLineTypography } from '@pflegenavi/web-components';
import { generatePath, nhAppMainPages } from '@pflegenavi/frontend/routing';
import type { CashTransactionTableRowModel } from '../../interfaces/CashTransactionTableRowModel';

interface ColumnProps {
  row: CashTransactionTableRowModel;
}

export const TypeColumn: FC<ColumnProps> = ({ row }) => (
  <Label
    variant="ghost"
    color={row.typeLabelColor}
    className={`cashListTransactionStatus${row.type}`}
  >
    {row.typeLabelString}
  </Label>
);

const ResidentLink: FC<{
  resident: { id: string; name: string };
  inTooltip?: boolean;
}> = ({ resident, inTooltip = false }) => (
  <Link
    variant="body2"
    noWrap={!inTooltip}
    color={inTooltip ? 'inherit' : undefined}
    sx={
      inTooltip
        ? { overflowWrap: 'anywhere' }
        : // Ellipsis needs a block box; an inline link overflows the cell.
          { display: 'block', minWidth: 0 }
    }
    to={generatePath(nhAppMainPages.RESIDENT_DETAIL_ACCOUNT, {
      resident_id: resident.id,
    })}
    // The row itself opens the transaction group. React events bubble
    // through the tooltip portal too, so this also covers tooltip links.
    onClick={(event) => event.stopPropagation()}
  >
    {resident.name}
  </Link>
);

export const ResidentColumn: FC<ColumnProps> = ({ row }) => {
  const [first, ...rest] = row.residents;
  if (!first) {
    return <Typography variant="body2">--</Typography>;
  }
  if (rest.length === 0) {
    return <ResidentLink resident={first} />;
  }
  return (
    <Tooltip
      arrow
      title={
        <Stack>
          {row.residents.map((resident) => (
            <ResidentLink key={resident.id} resident={resident} inTooltip />
          ))}
        </Stack>
      }
    >
      <Stack direction="row" sx={{ gap: 0.5, alignItems: 'baseline' }}>
        <ResidentLink resident={first} />
        <Typography variant="body2" sx={{ flexShrink: 0 }}>
          +{rest.length}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

export const DateColumn: FC<ColumnProps> = ({ row }) => (
  <Stack sx={{ gap: 1 }}>
    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1 }}>
      {row.dateString}
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1 }}>
      {row.timeString}
    </Typography>
  </Stack>
);

export const EmployeeColumn: FC<ColumnProps> = ({ row }) => (
  <Typography variant="body2">{row.employeeString}</Typography>
);

export const BalanceColumn: FC<ColumnProps> = ({ row }) => (
  <Typography variant="subtitle2">{row.balanceAfterString}</Typography>
);

export const AmountColumn: FC<ColumnProps> = ({ row }) => (
  <Typography variant="subtitle2" color={row.amountColor}>
    {row.amountString}
  </Typography>
);

export const FeesColumn: FC<ColumnProps> = ({ row }) => {
  const { t } = useTranslation();
  if (
    row.separateFeeAmountInCents === 0 &&
    row.immediateFeeAmountInCents === 0
  ) {
    return <Typography variant="body2">--</Typography>;
  }
  return (
    <Tooltip
      arrow
      placement="right"
      title={t('cashManagement.table-row.fees-tooltip', {
        separateFeeAmount: row.separateFeeAmountString,
        immediateFeeAmount: row.immediateFeeAmountString,
      })}
    >
      <Stack sx={{ gap: 0.5 }}>
        <OneLineTypography
          variant="subtitle2"
          color={row.separateFeeAmountColor}
        >
          {row.separateFeeAmountString}
        </OneLineTypography>
        <OneLineTypography
          variant="subtitle2"
          color={row.immediateFeeAmountColor}
        >
          {row.immediateFeeAmountString}
        </OneLineTypography>
      </Stack>
    </Tooltip>
  );
};
